import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Table from "../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdjustmentTypesRequest,
  getDeliveryQCOrdersRequest,
  getDeliveryQCOrdersSuccess,
} from "../../../redux/orders/slice";
import dayjs from "dayjs";
import OrderTableFlagsCell from "../../../components/order-table/flags-cell";
import OrderTableOrderIdCell from "../../../components/order-table/orderId-cell";
import OrderTableRushCell from "../../../components/order-table/rush-cell";
import OrderTableClientCell from "../../../components/order-table/client-cell";
import OrderTableStatusCell from "../../../components/order-table/status-cell";
import TableHeaderSelectField from "../../../components/table-header-select-field/table-header-select-field";
import { queueType, userType } from "../../../utils/Constant";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import AddInspectorAndClientIssueDialog from "../issues/add-inspector-client-issue/add-inspector-client-issue";
import AddIssueDialog from "../issues/add-issue/add-issue";
import AdvancedDatePicker from "../../../components/advanced-date-picker/advanced-date-picker";
import { splitCamelCaseToWords } from "../../../utils/Formatter";
import { deliverResultsRequest, downloadResultsRequest } from "../../../utils/APICalls";
import ErrorNotification from "../../../components/error-notification/error-notification";
import Notification from "../../../components/notification/notification";
import CloseIcon from "@mui/icons-material/Close";
import Loading from "../../../components/loading/loading";

const FeeInfo = ({ rowData, feeType }: any) => {
  const [fee, setFee] = useState<any>();
  const [feeWithAdjustment, setFeeWithAdjustment] = useState([]);
  const [amount, setAmount] = useState();

  const { adjustmentTypes } = useSelector((state: any) => state.orders);

  useEffect(() => {
    if (rowData) {
      if (feeType === "client") {
        const feeFilter = rowData?.fees.filter(
          (fee: any) =>
            fee?.category === "ClientFee" && fee.adjustmentType === null,
        );
        const feeWithAdjustment = rowData?.fees.filter(
          (fee: any) => fee.category === "ClientFee" && fee.adjustmentType,
        );
        const totalAmount = rowData?.fees.filter(
          (fee: any) =>
            fee?.category === "ClientFee"
        ).reduce(
          (total: number, fee: any) => total + (fee.amount ?? 0),
          0,
        );
        setFee(feeFilter[0]);
        setFeeWithAdjustment(feeWithAdjustment);
        setAmount(totalAmount);
      } else {
        const feeFilter = rowData?.fees.filter(
          (fee: any) =>
            fee?.category === "InspectorFee" && fee.adjustmentType === null,
        );
        const feeWithAdjustment = rowData?.fees.filter(
          (fee: any) => fee.category === "InspectorFee" && fee.adjustmentType,
        );
        const totalAmount = rowData?.fees.filter(
          (fee: any) =>
            fee?.category === "InspectorFee"
        ).reduce(
          (total: number, fee: any) => total + (fee.amount ?? 0),
          0,
        );
        setFee(feeFilter[0]);
        setFeeWithAdjustment(feeWithAdjustment);
        setAmount(totalAmount);
      }
    }
  }, [rowData]); // eslint-disable-line

  const getAdjustmentDisplayName = (adjustmentType: string) => {
    if (!adjustmentType) return adjustmentType;

    const match = adjustmentTypes?.find(
      (a: { adjustmentType: string; displayName: string }) =>
        a.adjustmentType === adjustmentType,
    );
    return match ? match.displayName : splitCamelCaseToWords(adjustmentType);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "inherit" }}>
      <Tooltip
        arrow
        placement="top"
        title={
          <Box>
            <Box
              sx={{ display: "flex", borderBottom: "2px solid white", py: 0.5 }}
            >
              <Typography sx={{ flex: 2, fontSize: 10, fontWeight: "510" }}>
                Type
              </Typography>
              <Typography sx={{ flex: 2, fontSize: 10, fontWeight: "510" }}>
                Adjustment
              </Typography>
              <Typography sx={{ flex: 1, fontSize: 10, fontWeight: "510" }}>
                Amount
              </Typography>
              <Typography sx={{ flex: 2, fontSize: 10, fontWeight: "510" }}>
                Note
              </Typography>
            </Box>
            <Box sx={{ display: "flex", py: 0.5 }}>
              <Typography sx={{ flex: 2, fontSize: 10 }}>
                {fee?.type}
              </Typography>
              <Typography sx={{ flex: 2, fontSize: 10 }}>
                {fee?.adjustmentType || "N/A"}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: 10, mx: 1 }}>
                ${Number(fee?.amount ?? 0).toFixed(2)}
              </Typography>
              <Typography sx={{
                flex: 2,
                fontSize: 10,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
              }}>
                {fee?.note || "-"}
              </Typography>
            </Box>
            {feeWithAdjustment.map((adjustedFee: any) => (
              <>
                <Divider sx={{ background: "white" }} />
                <Box sx={{ display: "flex", py: 0.5 }}>
                  <Typography sx={{ flex: 2, fontSize: 10 }}>
                    {adjustedFee.type === "PreBillingAdjustment"
                      ? "Pre-Billing Adjustment"
                      : splitCamelCaseToWords(adjustedFee.type)}
                  </Typography>
                  <Typography sx={{ flex: 2, fontSize: 10 }}>
                    {adjustedFee?.adjustmentType
                      ? getAdjustmentDisplayName(adjustedFee?.adjustmentType)
                      : "N/A"}
                  </Typography>
                  <Typography sx={{ flex: 1, fontSize: 10, mx: 1 }}>
                    {adjustedFee?.amount < 0 ? "-" : ""}
                    {`$${Math.abs(adjustedFee?.amount || 0).toFixed(2)}`}
                  </Typography>
                  <Typography sx={{
                    flex: 2,
                    fontSize: 10,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 3,
                    overflow: 'hidden',
                  }}>
                    {adjustedFee?.note || "-"}
                  </Typography>
                </Box>
              </>
            ))}
            <Box
              sx={{ display: "flex", py: 0.5, borderTop: "2px solid white" }}
            >
              <Typography sx={{ flex: 2 }}></Typography>
              <Typography sx={{ flex: 2 }}></Typography>
              <Typography sx={{ flex: 1, fontSize: 10, fontWeight: "510", mx: 1 }}>
                ${Number(amount ?? 0).toFixed(2)}
              </Typography>
              <Typography sx={{ flex: 2 }}></Typography>
            </Box>
          </Box>
        }
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(97, 97, 97, 0.9)",
              borderRadius: 1,
              minWidth: 400,
              boxShadow: 4,
            },
          },
          arrow: {
            sx: {
              color: "rgba(97, 97, 97, 0.9)",
            },
          },
        }}
      >
        <Typography noWrap fontSize={14}>
          ${Number(amount ?? 0).toFixed(2)}
        </Typography>
      </Tooltip>
    </Box>
  );
};

export default function DeliveryQCReview() {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeliverResultDialog, setShowDeliverResultDialog] = useState(false);
  const [isDeliverResults, setIsDeliverResults] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);


  const { loading, deliveryQCOrders } = useSelector(
    (state: any) => state.orders,
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  const rowCountRef = useRef(deliveryQCOrders?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (deliveryQCOrders?.totalCount !== undefined) {
      rowCountRef.current = deliveryQCOrders?.totalCount;
    }
    return rowCountRef.current;
  }, [deliveryQCOrders?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "flags",
      headerName: "Flags",
      width: 120,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableFlagsCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Flags</Typography>
      ),
    },
    {
      field: "orderNumber",
      headerName: "Order ID",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableOrderIdCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderid"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "orderStatus",
      headerName: "Status",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableStatusCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="orderStatus"
          sortField="status"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["UnassignedQC", "AssignedQC", "FinalQC", "ResultsDelivered"]}
          disabled={true}
        />
      ),
    },
    {
      field: "isRushInspection",
      headerName: "Rush?",
      width: 110,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableRushCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isRushInspection"
          sortField="isrushinspection"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Yes", "No"]}
        />
      ),
    },
    {
      field: "client",
      headerName: "Client",
      width: 180,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableClientCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientName"
          sortField="clientname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "formName",
      headerName: "Inspection Name",
      width: 200,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="formName"
          sortField="formname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "clientDueDate",
      headerName: "Client Due Date",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <AdvancedDatePicker
          field="clientDueDate"
          sortField="clientduedate"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "eta",
      headerName: "ETA",
      width: 100,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return (
          Math.ceil(dayjs(row?.clientDueDate).diff(Date.now(), "day", true)) ||
          ""
        );
      },
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>ETA</Typography>
      ),
    },
    {
      field: "loanNumber",
      headerName: "Loan #",
      width: 160,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row?.propertyRecord?.loanNumber || "-";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="loanNumber"
          sortField="loannumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "propertyName",
      headerName: "Property Name",
      width: 180,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value, row) => {
        return row?.propertyRecord?.propertyName || "-";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="propertyName"
          sortField="propertyname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "invoiceFrequency",
      headerName: "Invoice Frequency",
      width: 200,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="invoiceFrequency"
          sortField="invoiceFrequency"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Individual", "MonthlyBatch", "Project", "SemiMonthlyBatch"]}
        />
      ),
    },
    {
      field: "clientFee",
      headerName: "Client Fee Amount",
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <FeeInfo rowData={params?.row} feeType="client" />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientFees"
          sortField="clientfee"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          type="number"
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "inspectorFee",
      headerName: "Inspector Fee Amount",
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <FeeInfo rowData={params?.row} feeType="inspector" />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="inspectorFees"
          sortField="inspectorfee"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          type="number"
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "hasPreBillingAdjustment",
      headerName: "Pre-Billing Adjustments",
      width: 180,
      sortable: false,
      valueGetter: (value, row) => {
        const adjustmentFind = row?.fees.find(
          (data: any) => data?.adjustmentType,
        );
        return adjustmentFind ? "Yes" : "No";
      },
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="hasPreBillingAdjustment"
          sortField="hasprebillingadjustment"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Yes", "No"]}
        />
      ),
    },
    {
      field: "requester",
      headerName: "Requestor",
      width: 180,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value: any) => {
        return value?.name || "-";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="requester"
          sortField="requester"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "hasAttachments",
      headerName: "Attachments?",
      width: 180,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      valueGetter: (value: any) => {
        return value ? "Yes" : "No";
      },
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="hasAttachments"
          sortField="hasattachments"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Yes", "No"]}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    dispatch(getDeliveryQCOrdersSuccess({ response: null }));
    dispatch(getAdjustmentTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    setRowData(deliveryQCOrders?.items);
  }, [deliveryQCOrders]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getDeliveryQCOrdersRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("clientDueDate.Operator")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      queueType: queueType.qc,
      orderStatus: "AssignedQC",
      page: 1,
      pageSize: 10,
    } as any);
    setFilterResetKey((prev) => prev + 1);
  };

  const onDownloadResultsClick = async () => {
    const selectedRows = rowData.filter((row: any) =>
      rowSelectionModel.includes(row.id)
    );
    const orderIds = selectedRows
      .map((row: any) => row.orderId || row.id)
      .filter(Boolean);

    if (!orderIds.length) return;

    try {
      setIsDownloading(true);
      const response = await downloadResultsRequest(
        orderIds as string[]
      );
      if (response && response.status === 200) {
        const mimeType = response.headers["content-type"] || "application/zip";
        const blob = new Blob([response.data], {
          type: mimeType,
        });

        const contentDisposition = response.headers["content-disposition"];
        let fileName = "Results";
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", fileName);
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (
        response &&
        (response.status === 400 || response.status === 404)
      ) {
        setShowErrorNotification(true);
        setErrorMessage(response?.detail);
      }
    } catch {
      // optional: could hook into global error notification
    } finally {
      setIsDownloading(false);
    }
  };

  const deliverResults = async () => {
    const selectedRows = rowData.filter((row: any) =>
      rowSelectionModel.includes(row.id)
    );
    const orderIds = selectedRows
      .map((row: any) => row.orderId || row.id)
      .filter(Boolean);

    if (!orderIds.length) return;

    try {
      setIsDeliverResults(true);
      const response = await deliverResultsRequest(
        orderIds as string[]
      );
      if (response && response.status === 204) {
        setShowDeliverResultDialog(false);
        setShowSuccessNotification(true);
        dispatch(getDeliveryQCOrdersRequest(filterParams));
        setRowSelectionModel([]);
      } else if (
        response &&
        (response.status === 400 || response.status === 404 || response.status === 403)
      ) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail);
      }
    } catch {
      // optional: could hook into global error notification
    } finally {
      setIsDeliverResults(false);
    }
  }

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          {currentUser?.userType === userType.tenant ? (
            <>
              <Button
                sx={{ fontWeight: "550" }}
                variant="text"
                disabled={
                  rowSelectionModel.length === 0 ||
                  currentUser?.userType !== userType.tenant
                }
                onClick={onDownloadResultsClick}
              >
                Download Results
              </Button>
              <Button
                sx={{ fontWeight: "550", ml: 1 }}
                variant="text"
                disabled={rowSelectionModel.length === 0}
                onClick={() => setShowDeliverResultDialog(true)}
              >
                Deliver Results
              </Button>
              <Divider orientation="vertical" sx={{ height: 34, mx: 0.5 }} />
              <IconButton
                aria-label="issue"
                disabled={rowSelectionModel.length === 0}
                onClick={() => setShowAddIssue(true)}
              >
                <FeedbackOutlinedIcon
                  color={
                    rowSelectionModel.length === 0 ? "disabled" : "primary"
                  }
                />
              </IconButton>
            </>
          ) : (
            <IconButton
              aria-label="issue"
              disabled={rowSelectionModel.length === 0}
              onClick={() => setShowAddIssue(true)}
            >
              <FeedbackOutlinedIcon
                color={rowSelectionModel.length === 0 ? "disabled" : "primary"}
              />
            </IconButton>
          )}
        </Box>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={"qc-review-assigned-qc-review-table-" + filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          loading={loading || isDownloading}
          columnDef={column}
          headerHeight={headerHeight}
          disableVirtualization={true}
          disableColumnMenu={true}
          disableColumnResize={false}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={true}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
            const selected = newRowSelectionModel.map((id: string) =>
              rowData.find((r: any) => r.id === id),
            );
            setSelectedRowData(selected);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>

      {showAddIssue && [
        currentUser?.userType === userType.tenant ? (
          <AddIssueDialog
            orders={selectedRowData}
            showDialog={showAddIssue}
            closeDialog={() => setShowAddIssue(false)}
          />
        ) : (
          <AddInspectorAndClientIssueDialog
            orders={selectedRowData}
            showDialog={showAddIssue}
            closeDialog={() => setShowAddIssue(false)}
          />
        ),
      ]}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title="Error"
          message={errorMessage}
        />
      )}

      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          message="Deliver Results successfully"
          onClose={() => setShowSuccessNotification(false)}
        />
      )}

      {isDeliverResults && (<Loading />)}

      {showDeliverResultDialog && (
        <Dialog
          open={showDeliverResultDialog}
          onClose={() => setShowDeliverResultDialog(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" sx={{ fontWeight: "510" }}>Deliver Results?</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setShowDeliverResultDialog(false)}
            sx={(theme) => ({
              position: "absolute",
              right: 12,
              top: 12,
              color: "rgba(0, 0, 0, 0.54)",
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <DialogContentText sx={{ color: "rgba(0, 0, 0, 0.87)", mt: -2 }}>
              Changing the status to Results Delivered indicates that the Inspection Results are ready for client delivery.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ mr: 1 }}>
            <Button variant="outlined" onClick={() => setShowDeliverResultDialog(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={deliverResults}>
              Deliver Results
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
